/**
 * Product Quick Add functionality for collection pages
 * Handles variant selection and pricing updates for product cards
 * Uses MutationObserver to handle dynamically loaded products
 */

(function() {
  'use strict';

  // Store for initialized product containers to avoid duplicate initialization
  const initializedContainers = new WeakSet();

  /**
   * Initialize a single ProductQuickAdd container
   * @param {HTMLElement} container - The ProductQuickAdd container element
   */
  function initializeProductQuickAdd(container) {
    // Skip if already initialized
    if (initializedContainers.has(container)) {
      return;
    }

    // Mark as initialized
    initializedContainers.add(container);

    // Get variant data from data attributes
    const variantDataEl = container.querySelector('[data-variant-json]');
    const metafieldDataEl = container.querySelector('[data-variant-metafields]');
    
    if (!variantDataEl) {
      console.warn('No variant data found for', container.id);
      return;
    }

    let variants, variantMetafields;
    
    try {
      variants = JSON.parse(variantDataEl.textContent || variantDataEl.dataset.variantJson);
      variantMetafields = metafieldDataEl ? JSON.parse(metafieldDataEl.textContent || metafieldDataEl.dataset.variantMetafields) : {};
    } catch (e) {
      console.error('Failed to parse variant data:', e);
      return;
    }

    // Get DOM elements
    const selects = container.querySelectorAll('select.product-option-select');
    
    // The form is now outside the container, in a sibling element
    const productVariantsSection = container.closest('.product-variants');
    const hiddenInput = productVariantsSection ? productVariantsSection.querySelector('input[name="id"]') : null;
    const submitButton = productVariantsSection ? productVariantsSection.querySelector('button[type="submit"]') : null;
    const productPriceEl = productVariantsSection ? productVariantsSection.querySelector('.product-price') : null;
    
    // Check if we found the necessary elements
    if (!hiddenInput || !submitButton) {
      console.warn('ProductQuickAdd: Could not find form elements for', container.id);
      return;
    }

    // Get money format
    const moneyFormat = window.theme?.settings?.moneyFormat || (window.Shopify?.money_format || '${{amount}}');

    /**
     * Update pricing display for selected variant
     * @param {Object} variant - The selected variant object
     */
    function updatePricing(variant) {
      if (!productPriceEl || !variant) return;

      const dpPercent = variantMetafields[variant.id] || 0;
      const regularPrice = variant.price;
      const discountCents = Math.round((regularPrice * dpPercent) / 100);
      const specialPrice = regularPrice - discountCents;
      const hasDiscount = dpPercent > 0;

      let priceHTML = '';
      
      if (variant.price > 0) {
        priceHTML = '<h6>';

        if (hasDiscount) {
          // Show discounted price with strikethrough original
          priceHTML += '<del class="compare-at-price">';
          priceHTML += formatMoney(regularPrice, moneyFormat);
          priceHTML += '</del>';
          priceHTML += '<span class="special-price money">';
          priceHTML += formatMoney(specialPrice, moneyFormat);
          priceHTML += '</span>';
          
          const discountPercent = Math.round(((regularPrice - specialPrice) * 100) / regularPrice);
          priceHTML += ' <span class="percent-off">' + discountPercent + '% OFF</span>';
          
        } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          // Use variant's compare_at_price if available
          priceHTML += '<del class="compare-at-price">';
          priceHTML += formatMoney(variant.compare_at_price, moneyFormat);
          priceHTML += '</del>';
          priceHTML += '<span class="special-price money">';
          priceHTML += formatMoney(variant.price, moneyFormat);
          priceHTML += '</span>';
          
          const savingsPercent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          priceHTML += ' <span class="percent-off">' + savingsPercent + '% OFF</span>';
          
        } else {
          // Show regular price only
          priceHTML += '<span class="money">';
          priceHTML += formatMoney(regularPrice, moneyFormat);
          priceHTML += '</span>';
        }

        priceHTML += '</h6>';
      }

      productPriceEl.innerHTML = priceHTML;
    }

    /**
     * Format money using Shopify format or fallback
     * @param {number} cents - Price in cents
     * @param {string} format - Money format string
     * @returns {string} Formatted price
     */
    function formatMoney(cents, format) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, format);
      }
      // Fallback formatting
      return '$' + (cents / 100).toFixed(2);
    }

    /**
     * Update variant selection based on selected options
     */
    function updateVariantSelection() {
      const selectedOptions = [];
      
      // Collect selected option values
      for (let i = 0; i < selects.length; i++) {
        const val = selects[i].value;
        if (!val) {
          // No selection made
          if (hiddenInput) hiddenInput.value = '';
          if (submitButton) {
            submitButton.disabled = true;
            submitButton.classList.add('disabled');
          }
          return;
        }
        selectedOptions.push(val);
      }

      // Find matching variant
      const matchedVariant = variants.find(function(variant) {
        return variant.option1 === selectedOptions[0] &&
               (selectedOptions.length < 2 || variant.option2 === selectedOptions[1]) &&
               (selectedOptions.length < 3 || variant.option3 === selectedOptions[2]);
      });

      if (matchedVariant) {
        // Update hidden input with variant ID
        if (hiddenInput) {
          hiddenInput.value = matchedVariant.id;
        }

        // Enable/disable submit button based on availability
        if (submitButton) {
          if (matchedVariant.available) {
            submitButton.disabled = false;
            submitButton.classList.remove('disabled');
            submitButton.textContent = submitButton.dataset.addText || 'Add to Cart';
          } else {
            submitButton.disabled = true;
            submitButton.classList.add('disabled');
            submitButton.textContent = submitButton.dataset.soldOutText || 'Sold Out';
          }
        }

        // Update pricing
        updatePricing(matchedVariant);
      } else {
        // No matching variant found
        if (hiddenInput) hiddenInput.value = '';
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.classList.add('disabled');
        }
      }
    }

    /**
     * Set default selections for products with 4-Day and With Required Resources
     */
    function setDefaultSelections() {
      // Check if first dropdown has 4-Day option
      let has4Day = false;
      let fourDaySelected = false;
      
      if (selects.length > 0) {
        const firstSelect = selects[0];
        const options = firstSelect.querySelectorAll('option');
        
        // Check if 4-Day exists and select it (prioritize exact "4-Day" over variations)
        let fourDayOption = null;
        let fourDayOnlineOption = null;
        
        options.forEach(function(option) {
          if (option.value.includes('4-Day')) {
            has4Day = true;
            if (option.value === '4-Day') {
              fourDayOption = option.value;
            } else if (option.value.includes('4-Day (Online)')) {
              fourDayOnlineOption = option.value;
            }
          }
        });
        
        // Prioritize exact "4-Day" match, fallback to "4-Day (Online)"
        if (fourDayOption) {
          firstSelect.value = fourDayOption;
          fourDaySelected = true;
        } else if (fourDayOnlineOption) {
          firstSelect.value = fourDayOnlineOption;
          fourDaySelected = true;
        }
      }
      
      // Auto-select "With Required Resources" in the following cases:
      // 1. If 4-Day is selected (existing logic)
      // 2. If product doesn't have 4-Day option but has "With Required Resources"
      if (fourDaySelected || !has4Day) {
        selects.forEach(function(select, index) {
          // Skip the first dropdown if 4-Day was already selected there
          if (index === 0 && fourDaySelected) return;
          
          const options = select.querySelectorAll('option');
          options.forEach(function(option) {
            if (option.value.includes('With Required Resources')) {
              select.value = option.value;
            }
          });
        });
      }
    }

    // Add event listeners to option selects
    selects.forEach(function(select) {
      select.addEventListener('change', function(e) {
        // If first dropdown changes to 4-Day, auto-select With Required Resources
        if (select === selects[0] && e.target.value.includes('4-Day')) {
          selects.forEach(function(otherSelect, index) {
            if (index === 0) return; // Skip first dropdown
            
            const options = otherSelect.querySelectorAll('option');
            options.forEach(function(option) {
              if (option.value.includes('With Required Resources')) {
                otherSelect.value = option.value;
              }
            });
          });
        }
        
        updateVariantSelection();
      });
    });

    // Initialize with default selections
    // Small delay to ensure DOM is fully ready
    setTimeout(function() {
      setDefaultSelections();
      updateVariantSelection();
    }, 0);
  }

  /**
   * Initialize all ProductQuickAdd containers on the page
   */
  function initializeAllContainers() {
    const containers = document.querySelectorAll('[id^="ProductQuickAdd-"]');
    containers.forEach(initializeProductQuickAdd);
  }

  /**
   * Set up MutationObserver to watch for new product cards
   */
  function setupObserver() {
    // Target the main collection container or body as fallback
    const targetNode = document.querySelector('.collection-products') || 
                      document.querySelector('#collection') || 
                      document.body;

    // Options for the observer
    const observerOptions = {
      childList: true,
      subtree: true
    };

    // Callback for mutations
    const observerCallback = function(mutationsList) {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check each added node
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if this node or its descendants contain ProductQuickAdd containers
              if (node.id && node.id.startsWith('ProductQuickAdd-')) {
                initializeProductQuickAdd(node);
              } else if (node.querySelectorAll) {
                const containers = node.querySelectorAll('[id^="ProductQuickAdd-"]');
                containers.forEach(initializeProductQuickAdd);
              }
            }
          });
        }
      }
    };

    // Create and start the observer
    const observer = new MutationObserver(observerCallback);
    observer.observe(targetNode, observerOptions);
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    // Initialize existing containers
    initializeAllContainers();
    
    // Set up observer for future containers
    setupObserver();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

  // Expose initialization function globally for manual triggering if needed
  window.theme = window.theme || {};
  window.theme.initProductQuickAdd = initializeAllContainers;
})();